import React, { useEffect, useRef, useState } from 'react';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Logo from '../../components/common/Logo/Logo';
import type { MedicalExamFile } from '../../types/medicalExams';
import { isValidIsoDate } from '../../utils/masks';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import ExamFileUploadField from './components/ExamFileUploadField';
import { MAX_MEDICAL_EXAM_FILES } from './medicalExams.constants';
import { hasValidExamDates, revokeLocalExamFile } from './medicalExams.utils';
import styles from './MedicalExams.module.css';

const MedicalExamsPage: React.FC = () => {
  const [files, setFiles] = useState<MedicalExamFile[]>([]);
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  const buildDateErrors = (nextFiles: MedicalExamFile[]): Record<string, string> => {
    const errors: Record<string, string> = {};
    nextFiles.forEach((file) => {
      if (!isValidIsoDate(file.examDate)) {
        errors[file.id] = 'Informe a data do exame.';
      }
    });
    return errors;
  };

  useEffect(() => () => {
    filesRef.current.forEach(revokeLocalExamFile);
  }, []);

  const clearFiles = () => {
    files.forEach(revokeLocalExamFile);
    setFiles([]);
    setDateErrors({});
  };

  const handleFilesChange = (nextFiles: MedicalExamFile[]) => {
    setSuccessMessage(null);
    setFiles(nextFiles);
    setDateErrors((current) => {
      const next: Record<string, string> = {};
      nextFiles.forEach((file) => {
        if (current[file.id] && !isValidIsoDate(file.examDate)) {
          next[file.id] = current[file.id];
        }
      });
      return next;
    });
  };

  const requestSend = () => {
    if (files.length === 0 || isSending) {
      return;
    }

    const errors = buildDateErrors(files);
    setDateErrors(errors);
    if (Object.keys(errors).length > 0 || !hasValidExamDates(files)) {
      return;
    }

    setShowSendConfirm(true);
  };

  const handleCancel = () => {
    if (files.length === 0) {
      setSuccessMessage(null);
      return;
    }

    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    clearFiles();
    setShowCancelConfirm(false);
    setSuccessMessage(null);
  };

  const handleSendConfirm = () => {
    if (files.length === 0 || isSending) {
      return;
    }

    setIsSending(true);
    setShowSendConfirm(false);
    setSuccessMessage('Exames enviados com sucesso. Obrigada por compartilhar seus documentos!');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    clearFiles();
    setIsSending(false);
  };

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Seus exames"
        />
        <p className={pageStyles.headerSubtitle}>
          Envie aqui os exames que o time pediu. Você pode anexar PDFs ou fotos
          (JPG e PNG) — até {MAX_MEDICAL_EXAM_FILES} arquivos por envio.
        </p>
        <p className={pageStyles.headerSubtitle}>
          Informe a data de cada exame e confira os arquivos antes de enviar.
        </p>
      </div>

      {successMessage && (
        <div className={styles.feedbackWrap}>
          <AlertBanner variant="success" message={successMessage} />
        </div>
      )}

      <div className={`${styles.centeredLayout} ${styles.listWithFixedSend}`}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard} ${styles.flatCard}`}>
          <div className={styles.uploadSection}>
            <ExamFileUploadField
              files={files}
              dateErrors={dateErrors}
              onChange={handleFilesChange}
              disabled={isSending}
            />

            {files.length === 0 && (
              <div className={styles.emptyWrap}>
                <EmptyState
                  message="Nenhum exame selecionado ainda. Adicione PDF ou imagem para começar."
                  icon="clinical_notes"
                  showImage={false}
                />
              </div>
            )}
          </div>
        </article>
      </div>

      <div className={`${pageStyles.footerFixed} ${styles.sendFooter}`}>
        <div className={styles.sendFooterInner}>
          <Button
            type="button"
            variant="outline"
            size="app"
            fullWidth
            disabled={isSending}
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="app"
            fullWidth
            iconRight="arrow_forward"
            disabled={files.length === 0 || isSending}
            onClick={requestSend}
          >
            Enviar exames
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={showCancelConfirm}
        title="Cancelar envio"
        message="Deseja descartar os arquivos selecionados?"
        confirmLabel="Descartar"
        cancelLabel="Voltar"
        variant="danger"
        onCancel={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
      />

      <ConfirmModal
        open={showSendConfirm}
        title="Enviar exames"
        message="Deseja mesmo enviar seus exames? Depois de confirmar, o time poderá revisar os arquivos que você anexou."
        confirmLabel="Enviar exames"
        cancelLabel="Revisar"
        variant="info"
        icon="send"
        loading={isSending}
        onCancel={() => {
          if (!isSending) {
            setShowSendConfirm(false);
          }
        }}
        onConfirm={handleSendConfirm}
      />
    </CheckoutContainer>
  );
};

export default MedicalExamsPage;
